<template>
  <v-card variant="outlined">
    <div class="lx-panel__head">
      <span class="lx-panel__title">{{ label }}</span>
      <span class="lx-panel__meta">Weekly · 52-week trajectory<span v-if="unit"> · {{ unit }}</span></span>
    </div>
    <div class="lx-chart__frame">
      <!-- the convention is stated up front: solid = realised, dashed = projected, shaded = interval -->
      <div class="lx-legend mb-3" aria-label="Chart legend">
        <span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--hist"></span>Observed</span>
        <span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--fcst"></span>Janus baseline</span>
        <span class="lx-legend__item" v-if="alternative && alternative.length"><span class="lx-legend__swatch lx-legend__swatch--alt"></span>Alternative scenario</span>
        <span class="lx-legend__item" v-if="consensus && consensus.length"><span class="lx-legend__swatch lx-legend__swatch--cons"></span>Market consensus</span>
        <span class="lx-legend__item" v-if="bandsDrawn"><span class="lx-legend__swatch lx-legend__swatch--band"></span>{{ bandLegend }}</span>
        <span class="lx-legend__item" v-if="origin"><span class="lx-legend__swatch lx-legend__swatch--origin"></span>Forecast origin {{ origin }}</span>
      </div>
      <div ref="container" class="lx-chart" style="height: 420px" role="img" :aria-label="ariaLabel"></div>
      <!-- text alternative for the essential information -->
      <table class="lx-sr-only">
        <caption>{{ ariaLabel }}</caption>
        <tr v-for="(p, i) in summaryPoints" :key="i"><td>{{ p[0] }}</td><td>{{ p[1] }}</td></tr>
      </table>
      <div class="d-flex justify-space-between flex-wrap mt-2">
        <h5>
          <template v-if="consensus && consensus.length">Market consensus source: <a href="https://tradingeconomics.com" target="_blank" rel="noopener">tradingeconomics.com</a></template>
          <template v-else>No market consensus path is available for this variable.</template>
        </h5>
        <span v-if="bandsDrawn" class="lx-method">{{ bandMethod }}</span>
      </div>
    </div>
  </v-card>
</template>

<script>
import anychart from "anychart";
import { formatValue, unitOf } from "../lib/format";

const CSS = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

export default {
  name: "ForecastChart",
  props: {
    label: { type: String, default: "" },
    observed: { type: Array, default: () => [] }, // [[date, value]]
    baseline: { type: Array, default: () => [] },
    alternative: { type: Array, default: () => [] },
    consensus: { type: Array, default: () => [] },
    // {level: {lower:[[date,v]], upper:[[date,v]]}} for the baseline; `altBands` same shape for the alternative
    bands: { type: Object, default: null },
    altBands: { type: Object, default: null },
    showBands: { type: Boolean, default: true },
    selectedLevel: { type: Number, default: 80 },
    bandMethod: { type: String, default: "" },
    origin: { type: String, default: "" },
  },
  data: () => ({ chart: null, raf: null }),
  computed: {
    unit() {
      return unitOf(this.label);
    },
    bandLevels() {
      if (!this.showBands || !this.bands) return [];
      return Object.keys(this.bands).map(Number).filter((l) => this.bands[l] && this.bands[l].lower && this.bands[l].lower.length).sort((a, b) => b - a);
    },
    bandsDrawn() {
      return this.bandLevels.length > 0;
    },
    bandLegend() {
      return `${this.bandLevels.slice().sort((a, b) => a - b).join(" / ")}% prediction interval`;
    },
    ariaLabel() {
      const last = this.baseline.length ? this.baseline[this.baseline.length - 1] : null;
      const lastAlt = this.alternative.length ? this.alternative[this.alternative.length - 1] : null;
      let s = `${this.label}: observed to ${this.origin || "n/a"}.`;
      if (last) s += ` Janus baseline at ${last[0]}: ${formatValue(this.label, last[1])}.`;
      if (lastAlt) s += ` Alternative at ${lastAlt[0]}: ${formatValue(this.label, lastAlt[1])}.`;
      return s;
    },
    summaryPoints() {
      const pick = (arr) => (arr.length ? [arr[0], arr[Math.floor(arr.length / 2)], arr[arr.length - 1]] : []);
      return [...pick(this.observed), ...pick(this.baseline), ...pick(this.alternative)].map((p) => [p[0], formatValue(this.label, p[1])]);
    },
  },
  watch: {
    label: "schedule",
    observed: "schedule",
    baseline: "schedule",
    alternative: "schedule",
    consensus: "schedule",
    bands: "schedule",
    altBands: "schedule",
    showBands: "schedule",
    selectedLevel: "schedule",
    origin: "schedule",
  },
  mounted() {
    this.render();
  },
  beforeUnmount() {
    this.dispose();
  },
  methods: {
    /** Coalesce prop changes into a single redraw per frame; hover never triggers this. */
    schedule() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.render();
      });
    },
    dispose() {
      if (this.chart) {
        try {
          this.chart.dispose();
        } catch (e) {
          /* already disposed */
        }
        this.chart = null;
      }
    },
    bandRows(level, source) {
      const b = source && source[level];
      if (!b) return [];
      const up = new Map(b.upper);
      return b.lower.filter((p) => up.has(p[0])).map((p) => ({ x: p[0], low: Number(p[1]), high: Number(up.get(p[0])) }));
    },
    /** Create the chart once with its static chrome; series are swapped in render(). */
    ensureChart() {
      if (this.chart) return this.chart;
      const chart = anychart.line();
      chart.tooltip().useHtml(true).displayMode("union").titleFormat("{%x}");
      chart.tooltip().fontFamily(CSS("--f-mono", "JetBrains Mono")).fontSize(11).background().fill(CSS("--text", "#0d1420")).stroke(null);
      chart.title(false);
      chart.background().fill(CSS("--bg-1", "#ffffff"));
      chart.padding([8, 8, 4, 0]);
      chart.yAxis().stroke(CSS("--line", "#dbe1ea"));
      chart.xAxis().stroke(CSS("--line", "#dbe1ea"));
      chart.yAxis().ticks(false);
      chart.xAxis().ticks().stroke(CSS("--line", "#dbe1ea"));
      chart.yAxis().labels().fontFamily(CSS("--f-mono", "JetBrains Mono")).fontSize(10).fontColor(CSS("--text-mute", "#94a0b4"));
      chart.xAxis().labels().fontFamily(CSS("--f-mono", "JetBrains Mono")).fontSize(10).fontColor(CSS("--text-mute", "#94a0b4"));
      chart.yGrid().enabled(true).stroke(CSS("--grid", "#e8ecf2"));
      chart.xGrid().enabled(false);
      chart.crosshair().enabled(true).yLabel(false).yStroke(null);
      chart.crosshair().xStroke(CSS("--line-strong", "#c3ccd9"), 1, "3 3");
      chart.legend().enabled(false); // the HTML legend above states the convention
      // tight y-axis so small differences between paths stay visible
      chart.yScale().minimumGap(0.1).maximumGap(0.1);
      chart.xScale().ticks().interval(4);
      chart.xAxis().labels().format(function () {
        return String(this.value).slice(0, 7);
      });
      chart.container(this.$refs.container);
      this.chart = chart;
      return chart;
    },
    /** Swap series/markers on the existing instance (no dispose → no loader races). */
    render() {
      if (!this.$refs.container) return;
      const chart = this.ensureChart();
      const label = this.label;
      chart.removeAllSeries();

      // explicit, chronologically sorted category axis: with an implicit ordinal
      // scale AnyChart orders categories by first appearance, which would put the
      // observed history after the forecast when the bands are drawn first
      const xs = new Set();
      for (const arr of [this.observed, this.baseline, this.alternative, this.consensus]) for (const p of arr || []) xs.add(p[0]);
      for (const src of [this.bands, this.altBands]) if (src) for (const l of Object.values(src)) for (const p of l.lower || []) xs.add(p[0]);
      chart.xScale().values([...xs].sort());
      chart.yAxis().labels().format(function () {
        return formatValue(label, this.value);
      });

      // Bands first so lines draw on top. Wider levels at the back.
      const colors = {
        base: { 95: CSS("--ser-band-95", "rgba(37,99,184,.07)"), 80: CSS("--ser-band-80", "rgba(37,99,184,.12)"), 50: CSS("--ser-band-50", "rgba(37,99,184,.2)") },
        alt: { 95: CSS("--ser-band-alt-95", "rgba(11,143,106,.06)"), 80: CSS("--ser-band-alt-80", "rgba(11,143,106,.1)"), 50: CSS("--ser-band-alt-50", "rgba(11,143,106,.16)") },
      };
      for (const level of this.bandLevels) {
        const rows = this.bandRows(level, this.bands);
        if (rows.length) {
          const s = chart.rangeArea(rows);
          s.name(`Baseline ${level}%`).fill(colors.base[level] || colors.base[80]).stroke(null).hovered().stroke(null);
          s.tooltip().format(function () {
            return `${level}%: ${formatValue(label, this.low)} – ${formatValue(label, this.high)}`;
          });
        }
        const altRows = this.bandRows(level, this.altBands);
        if (altRows.length) {
          const s2 = chart.rangeArea(altRows);
          s2.name(`Alternative ${level}%`).fill(colors.alt[level] || colors.alt[80]).stroke(null).hovered().stroke(null);
          s2.tooltip().enabled(false);
        }
      }

      const line = (data, name, color, thickness, dash) => {
        if (!data || !data.length) return null;
        const s = chart.line(data);
        s.name(name);
        s.stroke(dash ? { color, thickness, dash } : { color, thickness });
        s.tooltip().format(function () {
          return `<span style="color:${color}"><b>${name}: ${formatValue(label, this.value)}</b></span>`;
        });
        return s;
      };
      line(this.observed, "Observed", CSS("--ser-hist", "#0d1420"), 2, null);
      line(this.baseline, "Janus baseline", CSS("--ser-fcst", "#2563b8"), 2, "6 4");
      line(this.alternative, "Alternative scenario", CSS("--ser-alt", "#0b8f6a"), 2, "6 4");
      line(this.consensus, "Market consensus", CSS("--ser-cons", "#9a6c1f"), 1.5, "2 3");

      // forecast origin: the boundary between realised and projected
      const m = chart.lineMarker(0);
      const t = chart.textMarker(0);
      if (this.origin) {
        m.enabled(true).value(this.origin).stroke({ color: CSS("--origin", "#94a0b4"), thickness: 1, dash: "3 3" });
        t.enabled(true).value(this.origin).anchor("left-top").align("top").offsetX(6).offsetY(2).text("forecast origin");
        t.fontFamily(CSS("--f-mono", "JetBrains Mono")).fontSize(10).fontColor(CSS("--text-mute", "#94a0b4"));
      } else {
        m.enabled(false);
        t.enabled(false);
      }
      chart.draw();
    },
    /** SVG markup of the current chart (rendered locally, no export server). */
    toSvg(width = 1200, height = 520) {
      return this.chart ? this.chart.toSvg({ width, height }) : null;
    },
    /** Legacy PNG download through AnyChart's export pipeline. */
    saveAsPng(filename) {
      if (!this.chart) return false;
      this.chart.saveAsPng({ width: 1000, height: 500, quality: 0.3, filename });
      return true;
    },
  },
};
</script>
